import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  BookmarkIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';

interface AnalyticsData {
  progress: {
    totalMessages: number;
    studentMessages: number;
    characterMessages: number;
    guidanceRequests: number;
    checkpoints: number;
    sessionDuration: number;
  };
  learningMetrics: {
    objectivesProgress: Array<{
      objective: string;
      progress: number;
      evidence: string[];
    }>;
    communicationSkills: {
      empathy: number;
      clarity: number;
      professionalism: number;
      emotionalIntelligence: number;
    };
  };
  characterAnalysis: Array<{
    characterId: string;
    name: string;
    emotionalJourney: Array<{
      timestamp: Date;
      emotionalState: string;
      intensity: number;
    }>;
  }>;
}

interface AnalyticsDashboardProps {
  sessionId: string;
  onClose: () => void;
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ sessionId, onClose }) => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [sessionId]);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      const analyticsData = await apiService.getGuidedLearningAnalytics(sessionId);
      setAnalytics(analyticsData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  };

  const getSkillColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSkillIcon = (score: number) => {
    if (score >= 80) return <ArrowTrendingUpIcon className="h-4 w-4 text-green-600" />;
    if (score >= 60) return <ArrowTrendingUpIcon className="h-4 w-4 text-yellow-600" />;
    return <ArrowTrendingDownIcon className="h-4 w-4 text-red-600" />;
  };

  const getEmotionalStateColor = (state: string) => {
    switch (state) {
      case 'angry': return 'bg-red-100 text-red-800';
      case 'worried': return 'bg-yellow-100 text-yellow-800';
      case 'confused': return 'bg-orange-100 text-orange-800';
      case 'hopeful': return 'bg-green-100 text-green-800';
      case 'sad': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleExport = async (format: 'json' | 'csv' | 'pdf') => {
    try {
      setIsExporting(true);
      const blob = await apiService.exportGuidedLearningSession(sessionId, format);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `session-analytics-${sessionId}-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading analytics...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white">
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Session Analytics</h2>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <button
                onClick={() => handleExport('json')}
                disabled={isExporting}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                JSON
              </button>
            </div>
            <button
              onClick={() => handleExport('csv')}
              disabled={isExporting}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
              CSV
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Progress Overview */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ChartBarIcon className="h-5 w-5 mr-2" />
              Progress Overview
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{analytics.progress.totalMessages}</div>
                <div className="text-sm text-blue-800">Total Messages</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{analytics.progress.studentMessages}</div>
                <div className="text-sm text-green-800">Your Messages</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{analytics.progress.guidanceRequests}</div>
                <div className="text-sm text-purple-800">Guidance Requests</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">{analytics.progress.checkpoints}</div>
                <div className="text-sm text-orange-800">Checkpoints</div>
              </div>
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Session Duration</span>
                <span className="text-sm text-gray-900">{analytics.progress.sessionDuration} minutes</span>
              </div>
            </div>
          </div>

          {/* Communication Skills */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ArrowTrendingUpIcon className="h-5 w-5 mr-2" />
              Communication Skills
            </h3>
            
            <div className="space-y-4">
              {Object.entries(analytics.learningMetrics.communicationSkills).map(([skill, score]) => (
                <div key={skill} className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getSkillIcon(score)}
                    <span className="ml-2 text-sm font-medium text-gray-700 capitalize">
                      {skill.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className={`h-2 rounded-full ${score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${score}%` }}
                      ></div>
                    </div>
                    <span className={`text-sm font-medium ${getSkillColor(score)}`}>
                      {Math.round(score)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Learning Objectives */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <LightBulbIcon className="h-5 w-5 mr-2" />
              Learning Objectives Progress
            </h3>
            
            <div className="space-y-4">
              {analytics.learningMetrics.objectivesProgress.map((objective, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900">{objective.objective}</h4>
                    <span className={`text-sm font-medium ${getSkillColor(objective.progress)}`}>
                      {Math.round(objective.progress)}%
                    </span>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <div 
                      className={`h-2 rounded-full ${objective.progress >= 80 ? 'bg-green-500' : objective.progress >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${objective.progress}%` }}
                    ></div>
                  </div>
                  
                  {objective.evidence.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-600 mb-2">Evidence:</p>
                      <div className="space-y-1">
                        {objective.evidence.slice(0, 2).map((evidence, idx) => (
                          <p key={idx} className="text-xs text-gray-500 italic">
                            "{evidence}"
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Character Emotional Journey */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <UserGroupIcon className="h-5 w-5 mr-2" />
              Character Emotional Journey
            </h3>
            
            <div className="space-y-6">
              {analytics.characterAnalysis.map((character) => (
                <div key={character.characterId} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">{character.name}</h4>
                  
                  <div className="flex flex-wrap gap-2">
                    {character.emotionalJourney.map((emotion, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getEmotionalStateColor(emotion.emotionalState)}`}>
                          {emotion.emotionalState}
                        </span>
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-1">
                            <div 
                              className="bg-blue-500 h-1 rounded-full"
                              style={{ width: `${(emotion.intensity / 10) * 100}%` }}
                            ></div>
                          </div>
                          <span className="ml-1 text-xs text-gray-500">{emotion.intensity}/10</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
